import React, { useEffect, useMemo, useState } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import NotificationService from '../../services/notificationService';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  AreaChart,
  Area,
} from 'recharts';

export default function Dashboard() {
  const [statsLoading, setStatsLoading] = useState(true);
  const [attendanceLoading, setAttendanceLoading] = useState(true);
  const [distributionLoading, setDistributionLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);
  const [attendanceError, setAttendanceError] = useState(null);
  const [distributionError, setDistributionError] = useState(null);
  const [stats, setStats] = useState([]);
  const [courses, setCourses] = useState([]);
  const [allCoursesAttendanceData, setAllCoursesAttendanceData] = useState([]);
  const [courseAttendanceData, setCourseAttendanceData] = useState([]);
  const [batchCountData, setBatchCountData] = useState([]);
  const [bengCountData, setBengCountData] = useState([]);
  const [bengCncsCountData, setBengCncsCountData] = useState([]);
  const [l3CountData, setL3CountData] = useState([]);
  const [citCountData, setCitCountData] = useState([]);
  const [englishDiplomaCountData, setEnglishDiplomaCountData] = useState([]);
  const [hndBusinessCountData, setHndBusinessCountData] = useState([]);
  const [baBusinessAdminCountData, setBaBusinessAdminCountData] = useState([]);
  const [l7SmlCountData, setL7SmlCountData] = useState([]);

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);

  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedCourse, setSelectedCourse] = useState('hnd');
  const [selectedAttendanceCourses, setSelectedAttendanceCourses] = useState(['all']);
  const [isCourseFilterModalOpen, setIsCourseFilterModalOpen] = useState(false);
  const [courseSearchQuery, setCourseSearchQuery] = useState('');

  const Spinner = ({ size = 10, color = '#10B981' }) => (
    <div className="flex items-center justify-center py-6">
      <div
        className="animate-spin rounded-full border-4 border-gray-200"
        style={{ width: size * 4, height: size * 4, borderTopColor: color }}
      />
    </div>
  );

  const user = AuthService.getUser();
  const userId = user?.superAdmin?.superAdminId;
  const userName = user?.superAdmin?.staff?.name || user?.superAdmin?.username || 'User';
  const isSuperAdmin = !!user?.superAdmin?.superAdminId || user?.superAdmin?.staff?.type === 'admin';
  const hasFullAccess = isSuperAdmin;

  useEffect(() => {
    let cancelled = false;

    const loadUnscheduledBatchNotification = async () => {
      if (!userId) return;

      const todayStr = new Date().toISOString().slice(0, 10);
      const sessionKey = `unscheduled-batches-notified-${todayStr}`;
      if (sessionStorage.getItem(sessionKey)) return;

      try {
        const res = await axiosInstance.get('/admin/timetable/unscheduled-batches', {
          params: { date: todayStr },
        });
        if (cancelled) return;

        const missing = Array.isArray(res?.data?.unscheduledBatches) ? res.data.unscheduledBatches : [];
        const scheduleType = res?.data?.scheduleType || 'weekday';
        if (missing.length === 0) return;

        const byCourse = {};
        missing.forEach((batch) => {
          const courseName = batch.courseName || 'Unknown Course';
          const batchName = batch.batchInternalName || batch.batchName;
          if (!byCourse[courseName]) byCourse[courseName] = [];
          byCourse[courseName].push(batchName);
        });

        const courseCount = Object.keys(byCourse).length;
        const title = `${missing.length} ${scheduleType} batch${missing.length > 1 ? 'es' : ''} unscheduled — ${todayStr}`;
        const message = `${courseCount} course${courseCount > 1 ? 's' : ''} affected today. Open to review and assign timetable entries.`;
        const link = `/admin/notifications/unscheduled-schedules?date=${encodeURIComponent(todayStr)}`;
        const localNotification = {
          id: `local-unscheduled-${todayStr}`,
          title,
          message,
          type: 'warning',
          link,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        sessionStorage.setItem(sessionKey, 'pending');

        NotificationService.saveLocalNotification(userId, localNotification);
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));

        await axiosInstance.post('/admin/timetable/notify-unscheduled', { date: todayStr, userId });
        if (cancelled) return;

        sessionStorage.setItem(sessionKey, 'sent');
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));
      } catch (_) {
        sessionStorage.removeItem(`unscheduled-batches-notified-${new Date().toISOString().slice(0, 10)}`);
      }
    };

    loadUnscheduledBatchNotification();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  // ── Live notification: batches with 0 < scheduledHours < 3 ───────────────
  useEffect(() => {
    let cancelled = false;

    const loadLowHoursNotification = async () => {
      if (!userId) return;

      const todayStr = new Date().toISOString().slice(0, 10);
      const sessionKey = `low-hours-batches-notified-${todayStr}`;
      if (sessionStorage.getItem(sessionKey)) return;

      try {
        const res = await axiosInstance.get('/admin/timetable/low-hours-batches', {
          params: { date: todayStr },
        });
        if (cancelled) return;

        const batches = Array.isArray(res?.data?.batches) ? res.data.batches : [];
        if (batches.length === 0) return;

        const byCourse = {};
        batches.forEach((batch) => {
          const courseName = batch.courseName || 'Unknown Course';
          if (!byCourse[courseName]) byCourse[courseName] = [];
          byCourse[courseName].push(batch.batchInternalName || batch.batchName);
        });

        const courseCount = Object.keys(byCourse).length;
        const title = `${batches.length} batch${batches.length > 1 ? 'es' : ''} scheduled with less than 3 hours — ${todayStr}`;
        const message = `${courseCount} course${courseCount > 1 ? 's' : ''} affected today. Open to review scheduled hours.`;
        const link = `/admin/notifications/low-hours-schedules?date=${encodeURIComponent(todayStr)}`;
        const localNotification = {
          id: `local-low-hours-${todayStr}`,
          title,
          message,
          type: 'warning',
          link,
          isRead: false,
          createdAt: new Date().toISOString(),
        };

        sessionStorage.setItem(sessionKey, 'pending');
        NotificationService.saveLocalNotification(userId, localNotification);
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));

        await axiosInstance.post('/admin/timetable/notify-low-hours', { date: todayStr, userId });
        if (cancelled) return;

        sessionStorage.setItem(sessionKey, 'sent');
        window.dispatchEvent(new CustomEvent('kaaryaalaya:notif-refresh'));
      } catch (_) {
        sessionStorage.removeItem(`low-hours-batches-notified-${new Date().toISOString().slice(0, 10)}`);
      }
    };

    loadLowHoursNotification();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;

        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
      } catch (e) {
        console.error('Failed to load coordinate courses for Dashboard', e);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchStats = async () => {
      setStatsLoading(true);
      setStatsError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/stats');
        if (!mounted) return;
        const d = resp?.data || {};
        setStats(d.stats || []);
      } catch (e) {
        if (!mounted) return;
        setStatsError(e?.response?.data?.message || 'Failed to load dashboard stats');
      } finally {
        if (mounted) setStatsLoading(false);
      }
    };

    const fetchAttendance = async () => {
      setAttendanceLoading(true);
      setAttendanceError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/attendance');
        if (!mounted) return;
        const d = resp?.data || {};
        setCourses(d.courses || []);
        setAllCoursesAttendanceData(d.allCoursesAttendanceData || []);
        setCourseAttendanceData(d.courseAttendanceData || []);
      } catch (e) {
        if (!mounted) return;
        setAttendanceError(e?.response?.data?.message || 'Failed to load dashboard attendance');
      } finally {
        if (mounted) setAttendanceLoading(false);
      }
    };

    const fetchDistribution = async () => {
      setDistributionLoading(true);
      setDistributionError(null);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/course-distribution');
        if (!mounted) return;
        const d = resp?.data || {};
        setBatchCountData(d.batchCountData || []);
        setBengCountData(d.bengCountData || []);
        setBengCncsCountData(d.bengCncsCountData || []);
        setL3CountData(d.l3CountData || []);
        setCitCountData(d.citCountData || []);
        setEnglishDiplomaCountData(d.englishDiplomaCountData || []);
        setHndBusinessCountData(d.hndBusinessCountData || []);
        setBaBusinessAdminCountData(d.baBusinessAdminCountData || []);
        setL7SmlCountData(d.l7SmlCountData || []);
      } catch (e) {
        if (!mounted) return;
        setDistributionError(e?.response?.data?.message || 'Failed to load dashboard course distribution');
      } finally {
        if (mounted) setDistributionLoading(false);
      }
    };

    fetchStats();
    fetchAttendance();
    fetchDistribution();

    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const iconMap = {
    users: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    'user-group': (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    'user-check': (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    calendar: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    'check-circle': (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return '🌅 Good Morning';
    if (hour < 17) return '☀️ Good Afternoon';
    return '🌙 Good Evening';
  };

  const formatDate = () =>
    currentTime.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const statsWithIcons = useMemo(() => (stats || []).map((s) => ({
    ...s,
    iconElement: iconMap[s.icon] || iconMap.users,
  })), [stats]);

  const allowedCourseIdsSet = useMemo(() => {
    if (!Array.isArray(accessibleCourseIds) || accessibleCourseIds.length === 0) return null;
    const set = new Set();
    accessibleCourseIds.forEach((id) => {
      const num = Number(id);
      if (Number.isFinite(num)) set.add(num);
    });
    return set;
  }, [accessibleCourseIds]);

  const filteredAttendanceCourses = useMemo(() => {
    const base = Array.isArray(courses) ? courses : [];
    if (!allowedCourseIdsSet || allowedCourseIdsSet.size === 0) return base;
    return base.filter((c) => {
      const numId = Number(c.courseId);
      return Number.isFinite(numId) && allowedCourseIdsSet.has(numId);
    });
  }, [courses, allowedCourseIdsSet]);

  const courseIdMapping = {
    hnd: [2, 3, 4, 5, 6, 7],
    beng: [1],
    bengCncs: [20],
    l3: [8, 9, 12, 13],
    cit: [10, 11],
    englishDiploma: [14, 15],
    hndBusiness: [16, 17],
    baBusiness: [18],
    l7Sml: [19],
  };

  const courseOptions = [
    { value: 'hnd', label: 'HND - IT Software Engineering & Network Engineering', data: batchCountData, type: 'dual', courseIds: courseIdMapping.hnd },
    { value: 'beng', label: 'BEng (Hons) in Software Engineering', data: bengCountData, type: 'single', courseIds: courseIdMapping.beng },
    { value: 'bengCncs', label: 'BEng (Hons) Computer Networking and Cloud Security', data: bengCncsCountData, type: 'single', courseIds: courseIdMapping.bengCncs },
    { value: 'l3', label: 'Level 3 Diploma in Information', data: l3CountData, type: 'single', courseIds: courseIdMapping.l3 },
    { value: 'cit', label: 'Certificate In Information Technology', data: citCountData, type: 'single', courseIds: courseIdMapping.cit },
    { value: 'englishDiploma', label: 'Diploma in English', data: englishDiplomaCountData, type: 'single', courseIds: courseIdMapping.englishDiploma },
    { value: 'hndBusiness', label: 'Pearson BTEC Level 5 HND in Business', data: hndBusinessCountData, type: 'single', courseIds: courseIdMapping.hndBusiness },
    { value: 'baBusiness', label: 'BA (Hons) in Business Administration', data: baBusinessAdminCountData, type: 'single', courseIds: courseIdMapping.baBusiness },
    { value: 'l7Sml', label: 'Level 7 BTEC Diploma in Strategic Management and Leadership', data: l7SmlCountData, type: 'single', courseIds: courseIdMapping.l7Sml },
  ].filter((course) => (course.data && course.data.length > 0));

  const currentCourse = courseOptions.find((c) => c.value === selectedCourse) || courseOptions[0];

  const currentAttendanceData = useMemo(() => {
    if (selectedAttendanceCourses.includes('all')) return allCoursesAttendanceData;
    const selected = courseAttendanceData.filter((c) => selectedAttendanceCourses.includes(String(c.courseId)));
    if (selected.length === 0) return [];
    if (selected.length === 1) return selected[0].data;
    const map = new Map();
    selected.forEach((cd) => {
      cd.data.forEach((item) => {
        const k = item.fullDate;
        if (map.has(k)) map.get(k).count += item.count; else map.set(k, { ...item });
      });
    });
    return Array.from(map.values()).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  }, [selectedAttendanceCourses, allCoursesAttendanceData, courseAttendanceData]);

  const selectedAttendanceCourseName = useMemo(() => {
    if (selectedAttendanceCourses.includes('all')) return hasFullAccess ? 'All Courses (Total Branch Visitors)' : 'All Accessible Courses';
    const names = [];
    selectedAttendanceCourses.forEach((idStr) => {
      const att = courseAttendanceData.find((c) => String(c.courseId) === idStr);
      if (att) names.push(att.courseName); else {
        const c = courses.find((x) => String(x.courseId) === idStr);
        if (c) names.push(c.fullName);
      }
    });
    if (names.length === 0) return 'No courses selected';
    if (names.length === 1) return names[0];
    return `${names.length} Courses Selected`;
  }, [selectedAttendanceCourses, courseAttendanceData, courses, hasFullAccess]);

  const totalAttendanceCount = currentAttendanceData.reduce((s, d) => s + (d.count || 0), 0);
  const avgAttendanceCount = currentAttendanceData.length ? Math.round(totalAttendanceCount / currentAttendanceData.length) : 0;
  const sectionErrors = [statsError, attendanceError, distributionError].filter(Boolean);

  useEffect(() => {
    if (courseOptions.length > 0) {
      const exists = courseOptions.some((c) => c.value === selectedCourse);
      if (!exists) setSelectedCourse(courseOptions[0].value);
    }
  }, [courseOptions, selectedCourse]);

  const EnhancedChartSection = ({ title, data, barColor, gradientId, description }) => {
    const totalStudents = data.reduce((sum, d) => sum + (d.count || 0), 0);
    return (
      <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h3 className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-2xl font-bold text-transparent">{title}</h3>
            {description && <p className="mt-2 text-sm text-gray-500">{description}</p>}
          </div>
          <div className="rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 px-6 py-3">
            <div className="text-center">
              <p className="text-xs font-medium text-gray-600">Total Students</p>
              <p className="mt-1 text-2xl font-bold" style={{ color: barColor }}>{totalStudents}</p>
            </div>
          </div>
        </div>
        <div className="mb-4 text-xs text-gray-600"><span className="font-medium">{data.length}</span> Active Batches</div>
        <ResponsiveContainer width="100%" height={360}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={barColor} stopOpacity={0.9} />
                <stop offset="95%" stopColor={barColor} stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 12, fill: '#6B7280' }} />
            <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
            <Tooltip cursor={{ fill: 'rgba(99, 102, 241, 0.1)' }} />
            <Legend wrapperStyle={{ paddingTop: '20px' }} />
            <Bar dataKey="count" name="Total Students" fill={`url(#${gradientId})`} radius={[8, 8, 0, 0]}>
              <LabelList dataKey="count" position="top" style={{ fontSize: '12px', fill: barColor, fontWeight: 'bold' }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (
    <DashboardLayout title="Dashboard" showStaffPresenceButton>
      {sectionErrors.length > 0 && (
        <div className="mb-6 space-y-3">
          {sectionErrors.map((msg, idx) => (
            <div key={`${msg}-${idx}`} className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800">{msg}</div>
          ))}
        </div>
      )}
      
      {/* Welcome Banner - Always show */}
      <div className="mb-8 rounded-2xl bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 p-8 text-white shadow-2xl">
            <div className="flex items-center justify-between">
              <div className="space-y-3">
                <h2 className="text-3xl font-bold tracking-tight">{getGreeting()}, {userName}!</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/30 px-4 py-2 text-sm font-semibold text-white backdrop-blur-sm border border-white/30 shadow-lg">
                    <span className="text-base">{isSuperAdmin ? '👑 Admin' : '👤 Staff Member'}</span>
                  </span>
                </div>
                <p className="text-lg text-indigo-100">{formatDate()}</p>
                <p className="mt-4 max-w-2xl text-base text-indigo-50">Here's an overview of your institution's performance and key metrics.</p>
              </div>
              <div className="hidden md:block">
                <div className="rounded-full bg-white/20 p-6 backdrop-blur-sm">
                  <svg className="h-16 w-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Section with Loader */}
          {statsLoading ? (
            <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 space-y-3">
                      <div className="h-4 w-24 animate-pulse rounded bg-gray-200"></div>
                      <div className="h-10 w-20 animate-pulse rounded bg-gray-300"></div>
                    </div>
                    <div className="h-16 w-16 animate-pulse rounded-2xl bg-gray-200"></div>
                  </div>
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {statsWithIcons.map((stat, index) => (
                <div key={stat.name} className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-lg transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl">
                  <div className="absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-10"></div>
                  <div className="relative flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                      <p className="mt-2 text-4xl font-bold text-gray-900 transition-all duration-300 group-hover:scale-110">{stat.value}</p>
                    </div>
                    <div className="rounded-2xl bg-indigo-600 p-4 text-white shadow-xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-12">{stat.iconElement}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mb-8 rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-2xl font-bold text-transparent">📊 Last 30 Days Attendance Trends</h3>
                <p className="mt-2 text-sm text-gray-500">{selectedAttendanceCourses.includes('all') ? 'Total unique visitors to the branch (deduplicated by student)' : selectedAttendanceCourses.length === 1 ? 'Course-specific attendance (unique students per day)' : `Combined attendance from ${selectedAttendanceCourses.length} selected courses`}</p>
              </div>
              <div className="sm:w-96">
                <div className="mb-2 flex items-center justify-between">
                  <label className="block text-sm font-medium text-gray-700">Filter by Course</label>
                  {hasFullAccess ? (
                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-800">All Courses</span>
                  ) : (
                    <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-800">Selected</span>
                  )}
                </div>
                <button type="button" onClick={() => setIsCourseFilterModalOpen(true)} className="flex w-full items-center justify-between rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm">
                  <span className="flex items-center gap-2">{selectedAttendanceCourses.includes('all') ? 'All Courses' : selectedAttendanceCourseName}</span>
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
              </div>
            </div>

            {attendanceLoading ? (
              <div className="rounded-2xl border border-dashed border-emerald-200 bg-emerald-50/60 p-10">
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {[1, 2, 3].map((k) => (
                    <div key={k} className="rounded-xl bg-white p-4 shadow-sm">
                      <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
                      <div className="mt-3 h-8 w-24 animate-pulse rounded bg-gray-300" />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center">
                  <Spinner size={12} />
                </div>
              </div>
            ) : (
              <>
                <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-teal-50 p-4"><p className="text-xs font-medium text-gray-600">Total Attendances (30 days)</p><p className="mt-1 text-3xl font-bold text-emerald-600">{totalAttendanceCount.toLocaleString()}</p></div>
                  <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-4"><p className="text-xs font-medium text-gray-600">Average Per Day</p><p className="mt-1 text-3xl font-bold text-blue-600">{avgAttendanceCount.toLocaleString()}</p></div>
                  <div className="rounded-xl bg-gradient-to-br from-purple-50 to-pink-50 p-4"><p className="text-xs font-medium text-gray-600">Peak Day Attendance</p><p className="mt-1 text-3xl font-bold text-purple-600">{Math.max(...currentAttendanceData.map((d) => d.count || 0), 0).toLocaleString()}</p></div>
                </div>

                <ResponsiveContainer width="100%" height={450}>
                  <AreaChart data={currentAttendanceData} margin={{ top: 20, right: 30, left: 20, bottom: 100 }}>
                    <defs>
                      <linearGradient id="colorAttendance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                    <XAxis dataKey="date" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 10, fill: '#6B7280' }} />
                    <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} label={{ value: 'Unique Students', angle: -90, position: 'insideLeft', style: { fill: '#6B7280' } }} />
                    <Tooltip cursor={{ stroke: '#10B981', strokeWidth: 2 }} />
                    <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={3} fill="url(#colorAttendance)" name="Unique Students" />
                  </AreaChart>
                </ResponsiveContainer>
              </>
            )}
          </div>

          {(distributionLoading || courseOptions.length > 0) && (
            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-lg">
                {distributionLoading ? (
                  <div className="space-y-4">
                    <div className="h-5 w-48 animate-pulse rounded bg-gray-200" />
                    <div className="h-4 w-64 animate-pulse rounded bg-gray-200" />
                    <div className="h-12 w-full animate-pulse rounded-xl bg-gray-100" />
                  </div>
                ) : (
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Select Course</h3>
                      <p className="mt-1 text-sm text-gray-500">Choose a course to view student distribution</p>
                    </div>
                    <div className="sm:w-96">
                      <select value={selectedCourse} onChange={(e) => setSelectedCourse(e.target.value)} className="block w-full rounded-xl border-2 border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-900 shadow-sm">
                        {courseOptions.map((course) => (
                          <option key={course.value} value={course.value}>{course.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {currentCourse && currentCourse.type === 'dual' && batchCountData && batchCountData.length > 0 && selectedCourse === 'hnd' && (
                <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-xl">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h3 className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-2xl font-bold text-transparent">{currentCourse.label}</h3>
                      <p className="mt-2 text-sm text-gray-500">Active students distributed across batches</p>
                    </div>
                    <div className="rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 px-6 py-3">
                      <div className="text-center">
                        <p className="text-xs font-medium text-gray-600">Total Students</p>
                        <p className="mt-1 text-2xl font-bold text-blue-600">{batchCountData.reduce((sum, d) => sum + ((d.seCount || 0) + (d.neCount || 0)), 0)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mb-4 flex items-center gap-6 text-sm text-gray-600">
                    <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-full bg-blue-500"></div><span className="font-medium">Software Engineering</span></div>
                    <div className="flex items-center gap-2"><div className="h-3 w-3 rounded-full bg-purple-500"></div><span className="font-medium">Network Engineering</span></div>
                    <div className="ml-auto text-xs"><span className="font-medium">{batchCountData.length}</span> Active Batches</div>
                  </div>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={batchCountData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                      <defs>
                        <linearGradient id="colorSE" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9} /><stop offset="95%" stopColor="#3B82F6" stopOpacity={0.7} /></linearGradient>
                        <linearGradient id="colorNE" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.9} /><stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.7} /></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} interval={0} tick={{ fontSize: 12, fill: '#6B7280' }} />
                      <YAxis allowDecimals={false} tick={{ fill: '#6B7280' }} />
                      <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
                      <Legend wrapperStyle={{ paddingTop: '20px' }} />
                      <Bar dataKey="seCount" name="SE Count" fill="url(#colorSE)" radius={[8, 8, 0, 0]}>
                        <LabelList dataKey="seCount" position="top" style={{ fontSize: '12px', fill: '#1E40AF', fontWeight: 'bold' }} />
                      </Bar>
                      <Bar dataKey="neCount" name="NE Count" fill="url(#colorNE)" radius={[8, 8, 0, 0]}>
                        <LabelList dataKey="neCount" position="top" style={{ fontSize: '12px', fill: '#6B21A8', fontWeight: 'bold' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'beng' && bengCountData && bengCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={bengCountData} barColor="#0EA5E9" gradientId="gradBeng" description="Student distribution across software engineering batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'bengCncs' && bengCncsCountData && bengCncsCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={bengCncsCountData} barColor="#10B981" gradientId="gradBengCncs" description="Student distribution across networking and cloud security batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'l3' && l3CountData && l3CountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={l3CountData} barColor="#22C55E" gradientId="gradL3" description="Student distribution across Level 3 diploma batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'cit' && citCountData && citCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={citCountData} barColor="#F97316" gradientId="gradCit" description="Student distribution across IT certificate batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'englishDiploma' && englishDiplomaCountData && englishDiplomaCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={englishDiplomaCountData} barColor="#A855F7" gradientId="gradEnglish" description="Student distribution across English diploma batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'hndBusiness' && hndBusinessCountData && hndBusinessCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={hndBusinessCountData} barColor="#14B8A6" gradientId="gradHndBusiness" description="Student distribution across HND Business batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'baBusiness' && baBusinessAdminCountData && baBusinessAdminCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={baBusinessAdminCountData} barColor="#F59E0B" gradientId="gradBaBusiness" description="Student distribution across business administration batches" />
              )}
              {currentCourse && currentCourse.type === 'single' && selectedCourse === 'l7Sml' && l7SmlCountData && l7SmlCountData.length > 0 && (
                <EnhancedChartSection title={currentCourse.label} data={l7SmlCountData} barColor="#EF4444" gradientId="gradL7Sml" description="Student distribution across Level 7 SML batches" />
              )}
            </div>
          )}

          {isCourseFilterModalOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm">
              <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
                <div className="border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50 px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="rounded-lg bg-emerald-500 p-2 text-white"><svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg></div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">Filter Attendance by Course</h3>
                        <p className="text-sm text-gray-600">Select one or more courses to view attendance data</p>
                      </div>
                    </div>
                    <button onClick={() => setIsCourseFilterModalOpen(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"><svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>
                  </div>
                </div>
                <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"><svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg></div>
                    <input type="text" value={courseSearchQuery} onChange={(e) => setCourseSearchQuery(e.target.value)} placeholder="Search courses by name or schedule type..." className="block w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20" />
                    {courseSearchQuery && (<button onClick={() => setCourseSearchQuery('')} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"><svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></button>)}
                  </div>
                </div>
                <div className="max-h-96 overflow-y-auto px-6 py-4">
                  {attendanceLoading ? (
                    <div className="space-y-2">
                      {[1, 2, 3, 4].map((k) => (
                        <div key={k} className="flex items-center gap-3 rounded-lg border-2 border-gray-200 bg-white p-4">
                          <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
                            <div className="h-3 w-24 animate-pulse rounded bg-gray-100" />
                          </div>
                          <div className="h-6 w-6 animate-pulse rounded-full bg-gray-200" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredAttendanceCourses
                        .filter((c) => {
                          if (!courseSearchQuery) return true;
                          const s = courseSearchQuery.toLowerCase();
                          return (c.fullName || '').toLowerCase().includes(s) || (c.scheduleType || '').toLowerCase().includes(s);
                        })
                        .map((course) => {
                          const idStr = String(course.courseId);
                          const isSelected = selectedAttendanceCourses.includes(idStr);
                          const hasAttendance = courseAttendanceData.some((c) => String(c.courseId) === idStr);
                          return (
                            <label key={course.courseId} className={`flex cursor-pointer items-center gap-3 rounded-lg border-2 p-4 ${isSelected ? 'border-emerald-300 bg-emerald-50' : 'border-gray-200 bg-white hover:border-emerald-200'}`}>
                              <input type="checkbox" checked={isSelected} onChange={(e) => {
                                if (e.target.checked) setSelectedAttendanceCourses((prev) => [...prev.filter((x) => x !== 'all'), idStr]);
                                else setSelectedAttendanceCourses((prev) => prev.filter((x) => x !== idStr));
                              }} className="h-5 w-5 rounded border-gray-300 text-emerald-600 focus:ring-2 focus:ring-emerald-500" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className={`font-semibold ${isSelected ? 'text-emerald-900' : 'text-gray-900'}`}>{course.fullName}</span>
                                  {course.scheduleType && (
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${String(course.scheduleType).toLowerCase() === 'weekday' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{String(course.scheduleType).toLowerCase() === 'weekday' ? '📅 Weekday' : '📆 Weekend'}</span>
                                  )}
                                  {!hasAttendance && (<span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">No data</span>)}
                                </div>
                                {!hasAttendance && <p className="mt-1 text-xs text-gray-500">No attendance records in the last 30 days</p>}
                              </div>
                              {isSelected && (
                                <div className="rounded-full bg-emerald-500 p-1"><svg className="h-5 w-5 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg></div>
                              )}
                            </label>
                          );
                        })}
                    </div>
                  )}
                </div>
                <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-sm text-gray-600">{selectedAttendanceCourses.length === 0 ? 'Please select at least one course' : selectedAttendanceCourses.includes('all') ? 'Viewing all courses combined' : `Viewing ${selectedAttendanceCourses.length} selected ${selectedAttendanceCourses.length === 1 ? 'course' : 'courses'}`}</p>
                    <div className="flex gap-3">
                      <button onClick={() => setIsCourseFilterModalOpen(false)} className="rounded-lg border-2 border-gray-300 bg-white px-6 py-2 font-semibold text-gray-700">Cancel</button>
                      <button onClick={() => setIsCourseFilterModalOpen(false)} disabled={selectedAttendanceCourses.length === 0} className="rounded-lg bg-emerald-600 px-6 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500">Apply Filter</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Add shimmer animation to Tailwind */}
          <style>{`
            @keyframes shimmer {
              100% {
                transform: translateX(100%);
              }
            }
          `}</style>

    </DashboardLayout>
  );
}
